import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import api from "../../lib/api";
import { ScrollReveal } from "../Reveal";

const CARD_HIDDEN = { opacity: 0, y: 30 };
const CARD_SHOWN = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true };

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  return (
    <section id="reviews" className="relative bg-navy py-24 md:py-40 border-t border-ivory/10" data-testid="reviews-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-12">
          <ScrollReveal>
            <h2 className="font-display font-light text-ivory text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
              What clients<br />
              <span className="italic text-gold">say.</span>
            </h2>
          </ScrollReveal>
          <div className="hidden md:flex gap-3">
            <button
              onClick={() => scroll(-1)}
              data-testid="reviews-prev"
              className="w-14 h-14 border border-ivory/20 text-ivory hover:border-gold hover:text-gold transition-colors font-mono-ui"
              aria-label="Previous"
            >
              ←
            </button>
            <button
              onClick={() => scroll(1)}
              data-testid="reviews-next"
              className="w-14 h-14 border border-ivory/20 text-ivory hover:border-gold hover:text-gold transition-colors font-mono-ui"
              aria-label="Next"
            >
              →
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex overflow-x-auto snap-x snap-mandatory gap-6 md:gap-8 px-6 md:px-10 pb-6 no-scrollbar"
        style={{ scrollbarWidth: "none" }}
        data-testid="reviews-scroller"
      >
        {reviews.map((r, idx) => (
          <motion.article
            key={r.id}
            initial={CARD_HIDDEN}
            whileInView={CARD_SHOWN}
            viewport={CARD_VIEWPORT}
            transition={{ duration: 0.8, delay: idx * 0.05 }}
            className="snap-start shrink-0 w-[85vw] md:w-[520px] border border-ivory/10 p-8 md:p-10 bg-navy-800/50 backdrop-blur-sm hover:border-gold/50 transition-colors duration-500"
            data-testid={`review-card-${idx}`}
          >
            <div className="flex items-center justify-between mb-8">
              <span className="font-mono-ui text-[10px] tracking-[0.3em] text-gold uppercase">
                No. {String(idx + 1).padStart(2, "0")}
              </span>
              <span className="text-gold text-sm tracking-widest">
                {"★".repeat(r.rating)}
              </span>
            </div>
            <blockquote className="font-display italic text-2xl md:text-3xl text-ivory leading-[1.2] tracking-tight">
              &ldquo;{r.quote}&rdquo;
            </blockquote>
            <footer className="mt-10 pt-6 border-t border-ivory/10">
              <div className="font-display text-xl text-ivory">{r.author}</div>
              <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-ivory/50 mt-1">
                {r.role}
              </div>
            </footer>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
